import type { GameContent, TowerConfig, WaveConfig, EnemyConfig, SkillConfig } from "@bastion/data";
import type { GameConfigRepository, ResolvedMap } from "./ConfigRepository";

/**
 * In-memory, immutable `GameConfigRepository` built from a validated
 * `GameContent` bundle. Core code only ever sees this interface, so the
 * bundle source (imported TS, JSON file, localStorage, future API) is
 * swappable without touching any system.
 */
export class InMemoryConfigRepository implements GameConfigRepository {
  private readonly maps: Record<string, ResolvedMap>;
  private readonly allTowers: Record<string, TowerConfig>;
  private readonly allSkills: Record<string, SkillConfig>;
  private readonly allEnemies: Record<string, EnemyConfig>;

  constructor(content: GameContent) {
    this.allTowers = content.towers;
    this.allSkills = content.skills;
    this.allEnemies = { ...content.enemies, ...content.bosses };
    this.maps = {};
    for (const map of Object.values(content.maps)) {
      const enemies: Record<string, EnemyConfig> = {};
      const waves: Record<string, WaveConfig> = {};

      for (const waveId of map.waves) {
        const wave = content.waves[waveId];
        if (!wave) throw new Error(`map "${map.id}" references unknown wave "${waveId}"`);
        waves[waveId] = wave;
        for (const group of wave.groups) {
          const enemy = content.enemies[group.enemyId] ?? content.bosses[group.enemyId];
          if (!enemy) {
            throw new Error(`wave "${wave.id}" references unknown enemy "${group.enemyId}"`);
          }
          enemies[group.enemyId] = enemy;
        }
      }

      const endlessConfig = content.endlessWaves?.[map.id];
      if (endlessConfig) {
        for (const candidate of [...endlessConfig.ordinaryRoster, ...endlessConfig.bossRoster]) {
          const enemy = content.enemies[candidate.enemyId] ?? content.bosses[candidate.enemyId];
          if (!enemy) {
            throw new Error(
              `endless config for map "${map.id}" references unknown enemy "${candidate.enemyId}"`,
            );
          }
          enemies[candidate.enemyId] = enemy;
        }
      }

      this.maps[map.id] = { map, towers: this.allTowers, enemies, skills: this.allSkills, waves };
    }
  }

  getMap(id: string): ResolvedMap | null {
    return this.maps[id] ?? null;
  }

  getTower(id: string): TowerConfig | null {
    return this.allTowers[id] ?? null;
  }

  getEnemy(id: string): EnemyConfig | null {
    return this.allEnemies[id] ?? null;
  }

  getWave(id: string): WaveConfig | null {
    for (const resolved of Object.values(this.maps)) {
      const found = resolved.waves[id];
      if (found) return found;
    }
    return null;
  }

  getSkill(id: string): SkillConfig | null {
    return this.allSkills[id] ?? null;
  }
}
